import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Content, FlexboxGrid } from 'rsuite';
import { PAYMENT_METHODS } from '../../components/payment/constants';
import FlowPageLayout from '../../components/layout/FlowPageLayout';
import PortalHeader from '../../components/layout/PortalHeader';
import { FLOWS_ENUM } from '../../common/enums';
import Payment from '../../components/payment/common/GenericPayment/Payment';
import {
  selectPaymentID,
  selectSelectedPaymentMethod,
} from '../../store/slices/payment/paymentSelectors';
import { checkApplicationPaymentAsync } from '../../store/slices/payment/paymentThunk';
import {
  selectCurrentStep,
  selectFlowSteps,
} from '../../store/slices/flow/flowSelectors';
import { selectApplicationNumber } from '../../store/slices/apply/applySelectors';
import {
  setCurrentStep,
  setStepCompletedStatus,
} from '../../store/slices/flow/flowSlice';
import { clearApplication } from '../../store/slices/apply/applySlice';
import { selectConfigSettings } from '../../store/slices/config/configSelectors';
import { isGeneric } from 'src/components/payment/common/utils';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';

const ApplyPayment = () => {
  const placeNavigate = usePlaceNavigate();
  const dispatch = useDispatch();

  const paymentID = useSelector(selectPaymentID);
  const selectedCurrentStep = useSelector(selectCurrentStep);
  const applicationNumber = useSelector(selectApplicationNumber);
  const selectedPaymentMethod = useSelector(selectSelectedPaymentMethod);
  const settings = useSelector(selectConfigSettings);
  const processor = settings.payment_processor;

  const redirectUrl = useMemo(() => {
    if (selectedPaymentMethod === PAYMENT_METHODS.CHECK) {
      return '/apply-success';
    }
    if (isGeneric(processor)) {
      return '/payment-redirect';
    }
    return '/apply-success';
  }, [processor, selectedPaymentMethod]);

  const onSuccess = useCallback(
    (omitCheck) => {
      if (!omitCheck) {
        dispatch(
          checkApplicationPaymentAsync(paymentID, placeNavigate(redirectUrl))
        );
      } else {
        placeNavigate(redirectUrl);
      }
    },
    [paymentID, dispatch, placeNavigate, redirectUrl]
  );

  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={24}>
        <PortalHeader flow={FLOWS_ENUM.APPLY_LICENSE} />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={24}>
        <Content as="div">
          <FlowPageLayout
            title="Payment"
            hideNextButton
            onBack={() => {
              dispatch(
                setStepCompletedStatus({
                  stepNum: selectedCurrentStep,
                  status: false,
                })
              );
              dispatch(setCurrentStep(selectedCurrentStep - 1));
              dispatch(clearApplication());
              placeNavigate('/apply-confirmation');
            }}
            flowProgressStepsSelector={selectFlowSteps}
            currentStep={selectedCurrentStep}
          >
            <Payment
              flow={FLOWS_ENUM.APPLY_LICENSE}
              onSuccess={onSuccess}
              permitIdentifier={applicationNumber}
              singlePaymentButton={isGeneric(processor)}
            />
          </FlowPageLayout>
        </Content>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default ApplyPayment;
